import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Bell } from 'lucide-react';
import { safeJson } from '../utils/api';
import { parseBackendDate } from '../utils/formatUtils';

export const NotificationService = {
    async requestPermission() {
        if (!('Notification' in window)) return false;
        if (Notification.permission === 'granted') return true;
        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return false;
    },

    async showNotification(title, options = {}) {
        const hasPermission = await this.requestPermission();
        if (hasPermission) {
            const { actions, ...supportedOptions } = options;
            const notification = new Notification(title, {
                icon: '/logo-pwa-96.png',
                badge: '/logo-pwa-96.png',
                tag: supportedOptions.tag || 'reminder',
                requireInteraction: true,
                silent: false,
                ...supportedOptions
            });

            notification.onclick = (event) => {
                event.preventDefault();
                window.focus();
                if (supportedOptions.data && supportedOptions.data.clientId) {
                    window.location.href = `/crm/clients/${supportedOptions.data.clientId}`;
                }
                notification.close();
            };

            // Auto-close after 15 seconds (longer for important reminders)
            setTimeout(() => notification.close(), 15000);
            return notification;
        }
        return null;
    },

    async checkReminders(currentUser, backendUrl) {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${backendUrl}/api/reminders?window=notification`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return;

            const reminders = await safeJson(response);
            const now = new Date();

            // Dedup tracking
            const shownKey = `shown_reminders_${currentUser?.id || 'default'}`;
            let shownReminders = {};
            try {
                shownReminders = JSON.parse(localStorage.getItem(shownKey) || '{}');
            } catch(e) {
                shownReminders = {};
            }

            // Clean old entries (>24h)
            const dayAgo = now.getTime() - 24 * 60 * 60 * 1000;
            Object.keys(shownReminders).forEach(key => {
                if (shownReminders[key] < dayAgo) delete shownReminders[key];
            });

            const upcomingReminders = reminders.filter(reminder => {
                if (reminder.status !== 'pending') return false;
                
                const reminderTime = parseBackendDate(reminder.scheduled_time);
                const minutesUntil = (reminderTime - now) / (1000 * 60);

                // [NOTIF-2026-05-28] Two trigger windows per user request:
                //   • 15-min reminder: window [13.5 .. 16] (single shot, deduped)
                //   • 3-min reminder:  window [1.5 .. 4] (single shot, deduped)
                //   • Missed catch-up: -10 .. 0 (still alert if not yet shown)
                const is15min = minutesUntil > 13.5 && minutesUntil <= 16;
                const is3min  = minutesUntil > 1.5  && minutesUntil <= 4;
                const isMissed = minutesUntil >= -10 && minutesUntil < 1.5;

                if (!is15min && !is3min && !isMissed) return false;

                const stage = is15min ? '15m' : (is3min ? '3m' : 'missed');
                const notifKey = `${stage}_${reminder.id}`;
                if (shownReminders[notifKey]) return false;

                shownReminders[notifKey] = now.getTime();
                reminder.__stage = stage;
                return true;
            });

            localStorage.setItem(shownKey, JSON.stringify(shownReminders));

            upcomingReminders.forEach(reminder => {
                const reminderTime = parseBackendDate(reminder.scheduled_time);
                const minutesUntil = Math.ceil((reminderTime - now) / (1000 * 60));
                const stage = reminder.__stage;

                let title, timeText;
                if (stage === '15m') {
                    title = `Через 15 мин: ${reminder.title}`;
                    timeText = `\nЧерез 15 минут`;
                } else if (stage === '3m') {
                    title = `Через 3 мин: ${reminder.title}`;
                    timeText = `\nЧерез 3 минуты`;
                } else {
                    // missed catch-up
                    title = minutesUntil < 0
                        ? `Пропущено: ${reminder.title}`
                        : `Напоминание: ${reminder.title}`;
                    timeText = minutesUntil < 0
                        ? `\n${Math.abs(minutesUntil)} мин. назад!`
                        : '\nСейчас!';
                }

                // Browser push notification
                this.showNotification(title, {
                        body: `${reminder.description || 'Запланированное действие'}\nКлиент: ${reminder.client_name}${timeText}`,
                        tag: `reminder_${reminder.id}_${stage}`,
                        data: { reminderId: reminder.id, clientId: reminder.client_id }
                    }
                );

                // Bell panel notification — pass `upcoming` flag = true for 15m, false for 3m/missed (more urgent)
                if (window.NotificationPanel && window.NotificationPanel.addReminderNotification) {
                    window.NotificationPanel.addReminderNotification(
                        reminder.title, reminder.client_name, reminder.client_id, stage === '15m'
                    );
                }
            });
        } catch (error) {
            // Silently fail - don't spam console on network errors
        }
    }
};

const NotificationManager = ({ currentUser, backendUrl }) => {
  const { tt } = useLanguage();
    const intervalRef = useRef(null);
    const lastCheckRef = useRef(0);

    useEffect(() => {
        if (!currentUser || !backendUrl) return;

        NotificationService.requestPermission();

        const doCheck = () => {
            const now = Date.now();
            // Prevent double-checks within 20 seconds (protects against tab wake-up bursts)
            if (now - lastCheckRef.current < 20000) return;
            lastCheckRef.current = now;
            NotificationService.checkReminders(currentUser, backendUrl);
        };

        // Check every 30 seconds for reliability
        intervalRef.current = setInterval(doCheck, 30 * 1000);

        // First check after 5 seconds
        const initialTimeout = setTimeout(doCheck, 5000);

        // Also check when tab becomes visible (handles background tab wake-up)
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                setTimeout(doCheck, 1000);
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            clearInterval(intervalRef.current);
            clearTimeout(initialTimeout);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [currentUser, backendUrl]);

    return null;
};

export const TestNotificationButton = ({ compact = false }) => {
    const handleTestNotification = async () => {
        const hasPermission = await NotificationService.requestPermission();
        if (hasPermission) {
            NotificationService.showNotification('Тест уведомлений', {
                body: 'Уведомления работают корректно! Вы будете получать напоминания о запланированных действиях.',
                icon: '/logo-pwa-96.png'
            });
        } else {
            alert(tt('Для получения уведомлений разрешите их показ в настройках браузера'));
        }
    };

    return (
        <button
            onClick={handleTestNotification}
            className={`${compact ? 'w-full' : ''} flex items-center p-2 rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800`}
            title={tt('Проверить работу уведомлений')}
            data-testid="test-notification-btn"
        >
            <Bell className="w-4 h-4" />
            {!compact && <span className="ml-2 text-sm">{tt('Тест')}</span>}
        </button>
    );
};

export default NotificationManager;
